/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.15.0
 */

import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { IPageOrderIdTranslation } from '../../modules/internationalization/types';
import { ECalendarPeriod } from '../../modules/internationalization/types/common/calendar';
import {
    EOrderIdParameterAdditions,
    EOrderIdParameterType,
    EOrderIdStatus,
    IOrderIdFile,
    IOrderIdInfo,
    IOrderIdInfoParameters,
} from './types';

/*
 * -------------------------------------------
 *               ORDER ID INFO
 * -------------------------------------------
 */

export interface IOrderIdInfoparamsResponse {
    accessLevel: string; // Уровень доступа
    account: string; // Лицевой счет
    actionName: string; // Действие
    additions?: {
        id: EOrderIdParameterAdditions;
        values: Array<{ href: string; value: string }>;
    };
    autoReport: {
        // Расписание
        dayOfPeriod: number; // 1-Пн, 2-Вт, 3-Ср, 4-Чт, 5-Пт, 6-Сб, 7-Вс
        periodType: ECalendarPeriod; // Тип периода: Ежедневно, Еженедельно, Ежемесячно
        reportId: string;
        startTime: string; // Время запуска отчета
    };
    bonus: string; // Вознаграждение (скорее всего устарело)
    dateFrom: {
        // Дата начала действия
        value: string;
        isCurrent: boolean;
    };
    dateFromMonth: {
        // Дата начала действия за период "месяц"
        value: string;
        isCurrent: boolean;
    };
    dateFrontQuarter: {
        // Дата начала действия за период "квартал"
        value: string;
        isCurrent: boolean;
    };
    dateTo: {
        // Дата окончания действия
        value: string;
        isCurrent: boolean;
    };
    dateToMonth: {
        // Дата окончания действия за период "месяц"
        value: string;
        isCurrent: boolean;
    };
    dateToQuarter: {
        // Дата окончания действия за период "квартал"
        value: string;
        isCurrent: boolean;
    };
    report: {
        // Отчет за период
        from: string;
        to: string;
    };
    sendparams: string[]; // Параметры отправки
    simCard: {
        iccid: string; // ICCID
        puk1: string; // PUK 1
    };
    transfer: {
        from: string; // ЛС отправителя
        to: string; // ЛС получателя
        amount: string; // Сумма перевода
    };
    tariff: {
        // Тарифный план
        href?: string; // Ссылка на тарифный план
        value?: string; // Название тарифного плана
    };
}

export interface IOrderIdInfoResponse {
    info: {
        action: string;
        actionId: number;
        count: number;
        date: string;
        status: EOrderIdStatus;
        user: string;
    };

    params: Partial<IOrderIdInfoparamsResponse>;

    isShowSubsList: boolean;
    subsWithErrors?: {
        count: number;
        exports: string[];
    };

    files?: IOrderIdFileResponse;
}

export const orderIdInfoResponseMapping = ({
    params,
    info,
    files,
    subsWithErrors,
    ...response
}: IOrderIdInfoResponse): IOrderIdInfo => {
    const additions: IOrderIdInfoParameters = params.additions
        ? {
              [mapAdditionalParameterToTranslationKey[params.additions.id]]: {
                  value: params.additions.values.map((addition) => ({
                      value: addition.value,
                      id: addition.href,
                      isExternal: true,
                      type: addition.href ? EOrderIdParameterType.link : EOrderIdParameterType.text,
                  })),
                  type: EOrderIdParameterType.list,
              },
          }
        : {};

    return {
        ...response,
        ...info,
        name: info.action,
        files: orderIdFileResponseMapping(files),
        subsCountWithErrors: subsWithErrors?.count ?? 0,
        exports: subsWithErrors?.exports ?? [],
        parameters: omitBy(
            {
                accessLevel: params.accessLevel ? { value: params.accessLevel } : undefined,
                account: params.account ? { value: params.account } : undefined,
                actionName: params.actionName ? { value: params.actionName } : undefined,

                autoReport: params.autoReport?.reportId
                    ? {
                          type: EOrderIdParameterType.autoReport,
                          value: '',
                          ...params.autoReport,
                      }
                    : undefined,

                bonus: params.bonus ? { value: params.bonus } : undefined,

                dateFrom: params.dateFrom ? { ...params.dateFrom, type: EOrderIdParameterType.date } : undefined,
                dateFromMonth: params.dateFromMonth
                    ? { ...params.dateFromMonth, type: EOrderIdParameterType.date }
                    : undefined,
                dateFrontQuarter: params.dateFrontQuarter
                    ? { ...params.dateFrontQuarter, type: EOrderIdParameterType.date }
                    : undefined,
                dateTo: params.dateTo ? { ...params.dateTo, type: EOrderIdParameterType.date } : undefined,
                dateToMonth: params.dateToMonth
                    ? { ...params.dateToMonth, type: EOrderIdParameterType.date }
                    : undefined,
                dateToQuarter: params.dateToQuarter
                    ? { ...params.dateToQuarter, type: EOrderIdParameterType.date }
                    : undefined,

                report: params.report
                    ? {
                          type: EOrderIdParameterType.datePeriod,
                          valueFrom: params.report.from,
                          valueTo: params.report.to,
                      }
                    : undefined,

                sendparams: params.sendparams
                    ? {
                          type: EOrderIdParameterType.list,
                          value: params.sendparams,
                      }
                    : undefined,

                simCardIccid: params.simCard ? { value: params.simCard.iccid } : undefined,
                simCardPuk1: params.simCard ? { value: params.simCard.puk1 } : undefined,

                transferFrom: params.transfer ? { value: params.transfer.from } : undefined,
                transferTo: params.transfer ? { value: params.transfer.to } : undefined,
                transferAmount: params.transfer ? { value: params.transfer.amount } : undefined,

                tariff: params.tariff,

                ...additions,
            },
            isNil,
        ),
    };
};

const mapAdditionalParameterToTranslationKey: {
    [key in EOrderIdParameterAdditions]: keyof IPageOrderIdTranslation['parameters']['fields'];
} = {
    [EOrderIdParameterAdditions.additionalAccount]: 'additionalAccounts',
    [EOrderIdParameterAdditions.dcRuleset]: 'rules',
    [EOrderIdParameterAdditions.externalService]: 'externalService',
    [EOrderIdParameterAdditions.favDirect]: 'favDirect',
    [EOrderIdParameterAdditions.rateOption]: 'tariffService',
    [EOrderIdParameterAdditions.rateService]: 'tariffService',
    [EOrderIdParameterAdditions.ratePlan]: 'tariff',
    [EOrderIdParameterAdditions.service]: 'service',
};

/*
 * -------------------------------------------
 *               ORDER ID FILE
 * -------------------------------------------
 */

export interface IOrderIdFileResponse {
    email: string; // Предустановленный email для модального окна
    files: Array<{ fileName: string; fileId: number }>;
    isCdmReport: boolean;
    isReportNotAvailable: boolean;
}

export const orderIdFileResponseMapping = (response?: IOrderIdFileResponse): IOrderIdFile =>
    response || { email: '', files: [], isCdmReport: false, isReportNotAvailable: false };
